import { Filter, Target, Sparkles, ImageIcon, Store, LayoutGrid, Bell, UserCheck, X } from 'lucide-react';

import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/molecules/sheet';
import { cn } from '@/lib/utils';

// ============ Types ============

interface CampaignFiltersSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: string;
  onStatusChange: (status: string) => void;
  type: string;
  onTypeChange: (type: string) => void;
}

// ============ Config ============

const STATUSES: Array<{ value: string; label: string; color?: string }> = [
  { value: 'all', label: 'Tous' },
  { value: 'active', label: 'Actives', color: 'bg-green-500' },
  { value: 'paused', label: 'En pause', color: 'bg-yellow-500' },
  { value: 'completed', label: 'Terminées', color: 'bg-blue-500' },
  { value: 'draft', label: 'Brouillons', color: 'bg-gray-400' },
];

const TYPES = [
  { value: 'all', label: 'Tous les types', icon: Target, category: null },
  { value: 'sponsored_product', label: 'Produit sponsorisé', icon: Sparkles, category: 'visibility' },
  { value: 'homepage_carousel', label: 'Carousel accueil', icon: ImageIcon, category: 'visibility' },
  { value: 'featured_shop', label: 'Boutique vedette', icon: Store, category: 'visibility' },
  { value: 'category_banner', label: 'Bannière catégorie', icon: LayoutGrid, category: 'visibility' },
  { value: 'push_shop_subscribers', label: 'Notif. abonnés', icon: UserCheck, category: 'notification' },
  { value: 'push_product_likers', label: 'Notif. fans produits', icon: Bell, category: 'notification' },
] as const;

// ============ Component ============

export function CampaignFiltersSheet({
  open,
  onOpenChange,
  status,
  onStatusChange,
  type,
  onTypeChange,
}: CampaignFiltersSheetProps) {
  const activeFiltersCount = (status !== 'all' ? 1 : 0) + (type !== 'all' ? 1 : 0);

  const handleReset = () => {
    onStatusChange('all');
    onTypeChange('all');
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl px-4 pb-8 max-h-[85vh] overflow-y-auto">
        <SheetHeader className="pb-4 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-lg">Filtres</SheetTitle>
            {activeFiltersCount > 0 && (
              <Button variant="ghost" size="sm" onClick={handleReset} className="text-muted-foreground">
                <X className="size-4 mr-1" />
                Réinitialiser
              </Button>
            )}
          </div>
        </SheetHeader>

        <div className="space-y-6 pt-4">
          {/* Status filter */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground">Statut</p>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s.value}
                  onClick={() => onStatusChange(s.value)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors',
                    status === s.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted hover:bg-muted/80'
                  )}
                >
                  {s.color && <span className={cn('size-2 rounded-full', s.color)} />}
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Type filter */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground">Type de campagne</p>
            <div className="space-y-1.5">
              {TYPES.map((typeItem) => {
                const Icon = typeItem.icon;
                const isSelected = type === typeItem.value;
                return (
                  <button
                    key={typeItem.value}
                    onClick={() => onTypeChange(typeItem.value)}
                    className={cn(
                      'flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-left transition-colors',
                      isSelected
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted'
                    )}
                  >
                    <Icon className="size-4 shrink-0" />
                    <span className="text-sm font-medium">{typeItem.label}</span>
                    {typeItem.category && (
                      <Badge
                        variant="secondary"
                        className={cn(
                          'ml-auto text-xs',
                          isSelected && 'bg-primary-foreground/20 text-primary-foreground'
                        )}
                      >
                        {typeItem.category === 'visibility' ? 'Visibilité' : 'Notification'}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Apply button */}
        <div className="pt-6">
          <Button
            onClick={() => onOpenChange(false)}
            className="w-full"
            size="lg"
          >
            Appliquer les filtres
            {activeFiltersCount > 0 && (
              <Badge variant="secondary" className="ml-2 bg-primary-foreground/20 text-primary-foreground">
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ============ Filter Button ============

interface FilterButtonProps {
  onClick: () => void;
  activeCount: number;
}

export function CampaignFilterButton({ onClick, activeCount }: FilterButtonProps) {
  return (
    <Button variant="outline" size="sm" onClick={onClick} className="gap-1.5">
      <Filter className="size-4" />
      Filtres
      {activeCount > 0 && (
        <Badge className="size-5 p-0 flex items-center justify-center text-xs">
          {activeCount}
        </Badge>
      )}
    </Button>
  );
}
